import { Course } from '../types';
import { normalizeCourseMetadata } from './courseMetadata';

export interface UniversityGroup {
  heading: string;
  subjects: readonly string[];
}

export interface UniversityArea {
  slug: string;
  name: string;
  detail: string;
  description: string;
  className: string;
  iconPath: string;
  groups: readonly UniversityGroup[];
  /** Subjects that belong on the page but not under one narrower heading. */
  umbrellaSubjects?: readonly string[];
}

export interface UniversitySection {
  heading: string;
  courses: Course[];
}

const UNIVERSITY_LEVEL = 'university';

export const universityAreas: readonly UniversityArea[] = [
  {
    slug: 'computer-science',
    name: 'Computer Science',
    detail: 'Systems · Code · Ideas',
    description: 'University courses in computing, from how systems work to building them.',
    className: 'computing',
    iconPath: 'M14 16 6 24l8 8M34 16l8 8-8 8M28 10 20 38',
    groups: [{ heading: 'Computer Science', subjects: ['Computer Science'] }],
  },
  {
    slug: 'economics-finance-and-management',
    name: 'Economics, Finance, and Management',
    detail: 'Economics · Finance · Management',
    description: 'University courses across economics, finance, and management. Each heading below is one part of that area.',
    className: 'efm',
    iconPath: 'M8 34V22M18 34V14M28 34V18M38 34V10M6 38h36',
    groups: [
      { heading: 'Economics', subjects: ['Economics'] },
      { heading: 'Finance', subjects: ['Finance', 'Accountancy'] },
      { heading: 'Management', subjects: ['Management'] },
    ],
    umbrellaSubjects: [
      'Economics Finance and Management',
      'Economics, Finance and Management',
      'Economics, Finance, and Management',
    ],
  },
  {
    slug: 'art',
    name: 'Art',
    detail: 'Looking · History · Ideas',
    description: 'University courses in looking at art and the history of art.',
    className: 'art',
    iconPath: 'M8 34V14l16-6 16 6v20M8 34l16 6 16-6M24 8v32',
    groups: [{ heading: 'Art', subjects: ['Art'] }],
  },
  {
    slug: 'english-literature',
    name: 'English Literature',
    detail: 'Texts · Criticism · Close reading',
    description: 'University courses in reading literature closely and testing an argument.',
    className: 'literature',
    iconPath: 'M7 8h14a6 6 0 0 1 6 6v27H13a6 6 0 0 1-6-6zM41 8H27v33h8a6 6 0 0 0 6-6z',
    groups: [{ heading: 'English Literature', subjects: ['English Literature'] }],
  },
];

export const universityAreaBySlug = (slug: string): UniversityArea | undefined =>
  universityAreas.find(area => area.slug === slug);

const hasSubject = (course: Course, subjects: readonly string[]): boolean => {
  const wanted = new Set(subjects.map(normalizeCourseMetadata));
  return course.subjects.some(subject => wanted.has(normalizeCourseMetadata(subject)));
};

export const isUniversityCourse = (course: Course): boolean =>
  !course.hidden && course.levels.some(level => normalizeCourseMetadata(level) === UNIVERSITY_LEVEL);

const byTitle = (a: Course, b: Course) =>
  a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' });

/** University courses for one area, grouped under the subject heading they match. */
export const universitySections = (area: UniversityArea, courses: Course[]): UniversitySection[] => {
  const available = courses.filter(isUniversityCourse);
  const sections = area.groups
    .map(group => ({
      heading: group.heading,
      courses: available.filter(course => hasSubject(course, group.subjects)).sort(byTitle),
    }))
    .filter(section => section.courses.length > 0);

  const umbrella = area.umbrellaSubjects;
  if (!umbrella) return sections;

  const placed = new Set(sections.flatMap(section => section.courses.map(course => course.id)));
  const rest = available
    .filter(course => hasSubject(course, umbrella) && !placed.has(course.id))
    .sort(byTitle);
  if (rest.length === 0) return sections;
  return [{ heading: area.name, courses: rest }, ...sections];
};
